import { Avatar, Button, Col, Form, Modal, Radio, Row, Space, Table, Typography, Upload, notification, Card, Statistic, Input } from "antd"
import { CheckCircleOutlined, CloseCircleOutlined, PaperClipOutlined, DollarOutlined, CreditCardOutlined, BankOutlined, SearchOutlined } from "@ant-design/icons"
import moment from "moment";
import { useEffect, useState, useRef } from "react";
import ajaxService from "../../../services/ajax-service";
import { Filter } from "../../../components";
import { statusBackgroundColor } from "../../../utils";
import { BASE_URL } from "../../../constants";
import { calculateModalWidth } from "../../../utils/widthCalculator";

const { Text, Title } = Typography

const formatTimeRange = (start, end) => {
    return `${moment(start).format('h:mm')}-${moment(end).format('h:mm A')}`;
};

const BUNDLE = 'bundle-subscription'
const PREMIUM = 'premium-subscription'

const Payments = () => {

    let [form] = Form.useForm()

    const [loading, setLoading] = useState(false)

    const [type, setType] = useState(PREMIUM)
    const [premium, setPremium] = useState(false)
    const [releasePayment, setReleasePayment] = useState(false)
    const [filter, setFilter] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const debounceTimer = useRef(null)
    const [showModal, setShowModal] = useState(false)
    const [data, setData] = useState({})
    const [dataSource, setDataSource] = useState({})
    const [fileList, setFileList] = useState([])

    const onFinish = async (values) => {

        setLoading(true)

        const formData = new FormData()
        formData.append('image', fileList[0].originFileObj)

        const response = await ajaxService.post('/release-payment/' + data.id, formData)
        if (response.success) {
            setLoading(false)
            form.resetFields()
            setFileList([])
            setShowModal(false)
            handleReleasePayment()
            notification.open({ message: "Payment released successfully", icon: <CheckCircleOutlined style={{ color: '#108ee9' }} /> })
        } else {
            setLoading(false)
            handleReleasePayment()
            if (!response.data) {
                notification.open({ message: response.message, icon: <CloseCircleOutlined style={{ color: 'red' }} /> })
            } else {
                for (const key in response.data) {
                    const message = JSON.stringify(response.data[key]).slice(1, -1).replace(/"/g, '');
                    notification.open({ message: message, icon: <CloseCircleOutlined style={{ color: 'red' }} /> })
                }
            }
        }
    }

    const onFinishFailed = (errorInfo) => {
        console.error('Failed : ' + errorInfo);
    }

    const handleChange = (info) => {
        let fileList = [...info.fileList];
        setFileList(fileList);
    };

    const handleClick = (id) => {
        const findData = dataSource.data.find(i => i.id === id)
        setData(findData)
        setShowModal(true)
    }

    const handleType = (newType) => {
        setType(newType)
        setFilter('')
        setSearchValue('')
        initialize(true, newType)
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'id',
            key: 'name',
            render: (text, record) => (
                <>
                    <Text style={{ fontWeight: 'bold' }}>{record.booked_by ? record.booked_by.name : record.user.name}</Text>
                    <br />
                    <Text>{record.booked_by ? record.booked_by.email : record.user.email}</Text>
                </>
            )
        },
        {
            title: 'Payment Date',
            dataIndex: 'payment_date',
            key: 'payment_date',
            render: (text, record) => {
                if (record.payment && record.payment[0]?.payment_date) {
                    return moment(record.payment[0].payment_date).format('DD-MMM-YYYY')
                } else if (record.payment_date) {
                    return moment(record.payment_date).format('DD-MMM-YYYY')
                } else {
                    return ''
                }
            }
        },
        {
            title: 'Court Name',
            dataIndex: 'court_name',
            key: 'court_name',
            hidden: true
        },
        {
            title: 'Payment',
            dataIndex: premium ? 'amount' : 'total_amount',
            key: 'total_amount',
            render: (amount) => <Text>{amount ?? "0.00"}</Text>
        },
        {
            title: releasePayment ? 'Actions' : 'Status',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status, record) => releasePayment ? (
                <Button type='primary' style={{ background: 'var(--themeColor)' }} onClick={() => handleClick(record.id)}>
                    Release
                </Button>
            ) : (
                <Button type='primary' size="small" style={{ background: statusBackgroundColor(status), width: 80, fontSize: 12, cursor: 'default', textTransform: 'uppercase' }}>
                    {status}
                </Button>
            ),
        },
    ];

    if (releasePayment) {
        columns.splice(1, 1)
    }

    if (premium) {
        columns.splice(4, 1)
    }

    const handlePremiumPayment = () => {
        setPremium(true)
        initialize(true)
    }

    const handleReleasePayment = async (name = null) => {
        setPremium(false)
        setReleasePayment(true)
        const response = await ajaxService.get(`/release-payment?name=${name ?? filter}`);
        if (response.success) {
            setDataSource(response)
        }
    }

    const fetchData = async (url, search = false, name = '') => {
        const response = await ajaxService.get(url);
        const { success, data } = response;

        if (success) {
            if (search) {
                if (data.data.length === 0) {
                    notification.open({ message: `${name} is not found!`, icon: <CloseCircleOutlined style={{ color: 'red' }} /> });
                }
            }

            setDataSource(data);
        }
    };

    const handleChangePage = async (page) => {
        const url = `/${premium ? "premium-payments?type=" + type + "&" : "court-payment?"}name=${filter}&page=${page}`;
        await fetchData(url);
    };

    const handleSearch = async ({ name }) => {
        const searchName = name ? name.trim() : '';
        setFilter(searchName);
        if (releasePayment) {
            handleReleasePayment(searchName);
        } else {
            const url = `/${premium ? "premium-payments?type=" + type + "&" : "court-payment?"}name=${searchName}`;
            await fetchData(url, true, searchName);
        }
    };

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            if (searchValue !== undefined) {
                handleSearch({ name: searchValue });
            }
        }, 500);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [searchValue]);

    const initialize = async (premium = false, filterType = type) => {
        const url = `/${premium ? "premium-payments?type=" + filterType + "&" : "court-payment?"}name=${filter}`;
        await fetchData(url);
    };

    useEffect(() => {
        initialize()
    }, [])

    const renderModal = (
        <Modal
            className="modal-overlay"
            width={calculateModalWidth('40%')}
            open={showModal}
            maskClosable={false}
            onCancel={() => setShowModal(false)}
            footer={null}
            centered
        >
            <div style={{ 
                padding: "var(--spacing-2xl) var(--spacing-xl)",
                background: 'var(--dark-bg-card)',
                borderRadius: 'var(--radius-lg)'
            }}>
                {/* Modal Header */}
                <div style={{ 
                    marginBottom: 'var(--spacing-xl)', 
                    textAlign: "center",
                    paddingBottom: 'var(--spacing-lg)',
                    borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
                }}>
                    <Title id='modal-title' level={3} style={{ 
                        color: 'var(--text-white)', 
                        margin: 0,
                        fontFamily: 'Poppins-Bold',
                        fontSize: '1.5rem'
                    }}>
                        {data.court_name}
                    </Title>
                </div>

                <Row gutter={[24, 24]}>
                    <Col span={6}>
                        <Avatar
                            size={90}
                            src={`${BASE_URL}/storage/${data.booked_by?.images?.[0]?.image || ''}`}
                        />
                    </Col>
                    <Col span={18}>
                        <Text style={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem',
                            color: 'var(--text-white)',
                            display: 'block',
                            marginBottom: 'var(--spacing-xs)'
                        }}>
                            {data.booked_by && data.booked_by.name}
                        </Text>
                        <Text style={{ 
                            fontWeight: 500,
                            color: 'var(--text-gray)',
                            display: 'block',
                            marginBottom: 'var(--spacing-xs)'
                        }}>
                            {data.booked_by && data.booked_by.email}
                        </Text>
                        <Text style={{ 
                            color: 'var(--text-gray-light)',
                            fontSize: '0.9rem'
                        }}>
                            {data.booked_by && data.booked_by.bio}
                        </Text>
                    </Col>

                    <Col span={24} style={{ marginTop: 'var(--spacing-md)' }}>
                        <div style={{ 
                            padding: 'var(--spacing-md)',
                            background: 'rgba(254, 2, 125, 0.05)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid rgba(254, 2, 125, 0.2)'
                        }}>
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <Text style={{ 
                                        fontWeight: 'bold',
                                        color: 'var(--text-white)'
                                    }}>
                                        Payment Date:
                                    </Text>
                                </Col>
                                <Col span={16}>
                                    <Text style={{ color: 'var(--text-gray)' }}>
                                        {data.payment && (data.payment).length > 0 ? moment(data.payment[0].payment_date).format('DD/MM/YYYY') : 'N/A'}
                                    </Text>
                                </Col>
                                <Col span={8}>
                                    <Text style={{ 
                                        fontWeight: 'bold',
                                        color: 'var(--text-white)'
                                    }}>
                                        Match Slot:
                                    </Text>
                                </Col>
                                <Col span={16}>
                                    <Text style={{ color: 'var(--text-gray)' }}>
                                        {data.slot && (data.slot).length > 0 ? formatTimeRange(data.slot[0].start_time, data.slot[0].end_time) : 'N/A'}
                                    </Text>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>

                <Form form={form} onFinish={onFinish} onFinishFailed={onFinishFailed}>
                    <div style={{ 
                        marginTop: 'var(--spacing-xl)',
                        paddingTop: 'var(--spacing-lg)',
                        borderTop: '1px solid rgba(254, 2, 125, 0.2)'
                    }}>
                        <Form.Item name="image" rules={[{ required: true, message: 'Attachment is required' }]}>
                            <Upload
                                maxCount={1}
                                fileList={fileList}
                                onChange={handleChange}
                                customRequest={(e) => e.onSuccess()}
                            >
                                <Button 
                                    icon={<PaperClipOutlined />}
                                    className="btn-secondary"
                                    style={{ height: '40px' }}
                                >
                                    Add Attachment
                                </Button>
                            </Upload>
                        </Form.Item>
                    </div>

                    {/* Modal Footer */}
                    <div style={{ 
                        textAlign: "center", 
                        marginTop: 'var(--spacing-xl)',
                        paddingTop: 'var(--spacing-lg)',
                        borderTop: '1px solid rgba(254, 2, 125, 0.2)',
                        display: 'flex',
                        gap: 'var(--spacing-md)',
                        justifyContent: 'center'
                    }}>
                        <Button
                            onClick={() => setShowModal(false)}
                            style={{
                                minWidth: '120px',
                                height: '40px',
                                background: 'var(--dark-bg-hover)',
                                borderColor: 'rgba(254, 2, 125, 0.3)',
                                color: 'var(--text-white)'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            htmlType="submit"
                            type="primary"
                            loading={loading}
                            className="btn-primary"
                            style={{ 
                                minWidth: '120px',
                                height: '40px'
                            }}
                        >
                            Release
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal >
    )

    return (
        <div style={{ padding: '24px', minHeight: '100vh' }}>
            {/* Header Section */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ color: 'var(--text-white)', margin: 0, fontSize: '2rem', fontFamily: 'Poppins-Bold' }}>
                    Payment Management
                </h1>
                <p style={{ color: 'var(--text-gray)', margin: '8px 0 0 0' }}>
                    Manage payments, releases, and transactions
                </p>
            </div>

            {/* Statistics Cards */}
            <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
                <Col xs={24} sm={12} md={6}>
                    <Card
                        style={{
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid var(--border-purple)',
                            borderRadius: '12px',
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        <Statistic
                            title={<span style={{ color: 'var(--text-gray)' }}>Total Payments</span>}
                            value={dataSource.total || 0}
                            valueStyle={{ color: 'var(--text-white)', fontSize: '2rem' }}
                            prefix={<DollarOutlined style={{ color: 'var(--primary-purple)' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card
                        style={{
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid var(--border-purple)',
                            borderRadius: '12px',
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        <Statistic
                            title={<span style={{ color: 'var(--text-gray)' }}>Pending Releases</span>}
                            value={dataSource.data?.filter(item => item.payment_status === 'pending').length || 0}
                            valueStyle={{ color: 'var(--text-white)', fontSize: '2rem' }}
                            prefix={<CreditCardOutlined style={{ color: 'var(--accent-purple)' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card
                        style={{
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid var(--border-purple)',
                            borderRadius: '12px',
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        <Statistic
                            title={<span style={{ color: 'var(--text-gray)' }}>Completed</span>}
                            value={dataSource.data?.filter(item => item.payment_status === 'completed').length || 0}
                            valueStyle={{ color: 'var(--text-white)', fontSize: '2rem' }}
                            prefix={<CheckCircleOutlined style={{ color: 'var(--green-positive)' }} />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card
                        style={{
                            backgroundColor: 'var(--card-bg)',
                            border: '1px solid var(--border-purple)',
                            borderRadius: '12px',
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        <Statistic
                            title={<span style={{ color: 'var(--text-gray)' }}>Total Revenue</span>}
                            value={dataSource.data?.reduce((sum, item) => sum + parseFloat(item.total_amount || item.amount || 0), 0).toFixed(2) || 0}
                            valueStyle={{ color: 'var(--text-white)', fontSize: '2rem' }}
                            prefix={<BankOutlined style={{ color: 'var(--red-negative)' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters and Search */}
            <Card
                style={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border-purple)',
                    borderRadius: '12px',
                    marginBottom: '24px',
                }}
                bodyStyle={{ padding: '24px' }}
            >
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Search by user name..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-gray)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value);
                                handleSearch({ name: e.target.value });
                            }}
                            allowClear
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.05)',
                                borderColor: 'var(--border-purple)',
                                color: 'var(--text-white)',
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        {premium &&
                            <Radio.Group onChange={(e) => handleType(e.target.value)} value={type}>
                                <Radio value={PREMIUM} style={{ color: 'var(--text-white)' }}>Premium</Radio>
                                <Radio value={BUNDLE} style={{ color: 'var(--text-white)' }}>Bundle</Radio>
                            </Radio.Group>
                        }
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Space>
                            {!releasePayment && (
                                <>
                                    <Button type="primary" style={{ background: 'var(--primary-purple)', borderColor: 'var(--primary-purple)', height: 35 }} onClick={() => handleReleasePayment()}>Release Court Payments</Button>
                                    {!premium && <Button type="primary" style={{ background: 'var(--primary-purple)', borderColor: 'var(--primary-purple)', height: 35 }} onClick={handlePremiumPayment}>View Premium Payments</Button>}
                                </>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Payments Table */}
            <Card
                style={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border-purple)',
                    borderRadius: '12px',
                }}
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={dataSource.data}
                    style={{ background: 'transparent' }}
                    rowKey='id'
                    pagination={releasePayment ? false : {
                        onChange: (page, size) => handleChangePage(page),
                        current: dataSource.current_page,
                        pageSize: dataSource.per_page,
                        total: dataSource.total,
                        style: { marginRight: '24px', marginBottom: '24px' },
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
                    }}
                />
            </Card>
            {renderModal}
        </div>
    )
}

export default Payments